import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { LogOut, UserCog, Eye, Square } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const LIVE_TICK_MS = 5000;

// Slim yellow banner shown at the very top whenever the current session is
// impersonating another user. Exit stops impersonating; Live turns on a
// read-only watch mode — a transparent shield blocks every click, tap and
// keystroke (only this banner stays usable), the dashboard re-fetches every
// few seconds via the "em:live-tick" event, and Escape stops watching.
const ImpersonationBanner = () => {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [live, setLive] = useState(false);

  // Live watch: block all keyboard input (Escape stops watching), drop any
  // focused field so the on-screen keyboard hides, and broadcast the tick.
  useEffect(() => {
    if (!live) return undefined;
    try { document.activeElement?.blur?.(); } catch { /* ignore */ }
    const block = (e) => {
      if (e.key === "Escape") { setLive(false); return; }
      e.preventDefault();
      e.stopPropagation();
    };
    window.addEventListener("keydown", block, true);
    window.addEventListener("keyup", block, true);
    window.addEventListener("keypress", block, true);
    const ticker = setInterval(() => {
      window.dispatchEvent(new CustomEvent("em:live-tick"));
    }, LIVE_TICK_MS);
    return () => {
      window.removeEventListener("keydown", block, true);
      window.removeEventListener("keyup", block, true);
      window.removeEventListener("keypress", block, true);
      clearInterval(ticker);
    };
  }, [live]);

  if (!user?.impersonating) return null;
  const real = user.real_user || {};

  const exit = async () => {
    setBusy(true);
    try {
      await api.post("/auth/impersonate/stop");
      toast.success("Back to your own account");
      // Reload so every tab/panel re-renders with the real user's role.
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not exit impersonation");
      setBusy(false);
    }
  };

  const toggleLive = () => {
    // Never side-effect inside a setState updater — StrictMode double-invokes
    // updaters and the toast would fire twice.
    const next = !live;
    setLive(next);
    // bottom-center: the banner's Stop/Exit buttons sit top-right and must
    // never be covered by this toast.
    toast[next ? "success" : "message"](
      next
        ? "Live watch on — you're read-only. Scroll works; Esc or Stop ends it."
        : "Live watch off",
      { position: "bottom-center" },
    );
  };

  // Read-only shield: covers everything below this banner. All pointer/touch
  // gestures are swallowed; wheel is forwarded to the dashboard scroller so
  // the watcher can still look up and down the page.
  const shield = live ? createPortal(
    <div
      data-testid="live-watch-shield"
      aria-hidden="true"
      className="fixed inset-0 cursor-not-allowed"
      style={{ zIndex: 85, touchAction: "none", background: "transparent" }}
      onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
      onPointerUp={(e) => { e.preventDefault(); e.stopPropagation(); }}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
      onDoubleClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
      onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); }}
      onWheel={(e) => {
        const sc = document.getElementById("dashboard-scroll");
        if (sc) sc.scrollBy({ top: e.deltaY, left: e.deltaX });
        else window.scrollBy({ top: e.deltaY, left: e.deltaX });
      }}
    />,
    document.body,
  ) : null;

  return (
    <div
      data-testid="impersonation-banner"
      className={`relative z-[95] flex shrink-0 items-center justify-between gap-3 border-b px-4 py-2 ${
        live
          ? "border-red-500/50 bg-red-500/10 text-red-100"
          : "border-amber-500/40 bg-amber-500/10 text-amber-100"
      }`}
    >
      <div className="flex min-w-0 items-center gap-2">
        <UserCog className={`h-4 w-4 shrink-0 ${live ? "text-red-300" : "text-amber-300"}`} />
        <span className="truncate text-xs sm:text-sm">
          Viewing as <span className="font-semibold text-white">{user.name || user.email}</span>
          <span className="hidden text-amber-200/70 sm:inline"> · signed in as {real.name || real.email}</span>
        </span>
        {live && (
          <span className="flex shrink-0 items-center gap-1.5 rounded-full border border-red-400/60 bg-red-500/20 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-widest text-red-200" data-testid="live-watch-indicator">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400/70" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
            </span>
            Live
          </span>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <button
          type="button"
          onClick={toggleLive}
          data-testid="impersonate-live-btn"
          aria-pressed={live}
          className={`flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-semibold uppercase tracking-wider ${
            live
              ? "border-red-400/70 bg-red-500/25 text-white hover:bg-red-500/35"
              : "border-amber-400/60 bg-amber-400/20 text-white hover:bg-amber-400/30"
          }`}
        >
          {live ? <Square className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          {live ? "Stop" : "Live"}
        </button>
        <button
          type="button"
          onClick={exit}
          disabled={busy}
          data-testid="exit-impersonation-btn"
          className="flex items-center gap-1.5 rounded-md border border-amber-400/60 bg-amber-400/20 px-2.5 py-1 text-xs font-semibold uppercase tracking-wider text-white hover:bg-amber-400/30 disabled:opacity-50"
        >
          <LogOut className="h-3.5 w-3.5" />
          {busy ? "Exiting…" : "Exit"}
        </button>
      </div>
      {shield}
    </div>
  );
};

export default ImpersonationBanner;
